import axios from 'axios';
import Vue from 'vue';
import { throttle, debounce } from 'lodash';
import App from './App.vue';
import router from './router';
import store from './store';
import MobileDetectMixin from './mixins/mobile-detect';
import CookieUtilsMixin from './mixins/cookie-utils';
import ScrollDirective from './directives/scroll';
import FOOTER from './data/footer';
import SOCIALS from './data/socials';
import PRIVACIES from './data/privacies';
import Analytics from './plugins/Analytics';
import GDPR from './plugins/GDPR';
import { ALL, APP_DETAIL, CONNECTOR_DETAIL } from './queries';

// Settings
Vue.config.productionTip = false;

// Plugins
Vue.use(Analytics);
Vue.use(GDPR);

// Directives
Vue.directive('scroll', ScrollDirective);

// Instantiation
new Vue({
  router,
  store,
  render: (h) => h(App),

  mixins: [
    MobileDetectMixin,
    CookieUtilsMixin,
  ],

  data() {
    return {
      // Constants
      footerItems: FOOTER,
      socials: SOCIALS,
      privacies: PRIVACIES,

      // Search View Data
      isSearchViewDataCached: false,
      appsData: null,
      connectorsData: null,
      filtersData: null,
      loadingApps: false,
      cachedSortValue: null,

      // Detail Data
      detailData: null,
      optimisticDetailData: null,
      detailViewBackRoute: null,

      // Environment Data
      userIsCustomer: this.cookieExists('_pubweb_is_cus'),
      isMobileDevice: this.isPhoneOrTablet(),
      window: {
        width:  window.innerWidth,
        height: window.innerHeight,
      },
    };
  },

  computed: {
    allAppsData() {
      const apps = this.appsData || [];
      const connectors = this.connectorsData || [];
      return [...apps, ...connectors];
    },
  },

  watch: {
    $route(to, from) {
      this.handleAppDetailViewWatch(to, from);
      this.handleSearchViewWatch(to);
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleWindowResize);

    const trackSearch = debounce((search) => {
      this.$analytics.trackSearch({ search });
    }, 1500);

    this.$router.afterEach((to, from) => {
      if ((to.path !== from.path) || (to.name === 'home')) {
        this.$analytics.trackPage({
          visitorType: (this.cookieExists('_pubweb_is_cus'))
            ? 'standard or free'
            : 'prospect',
          pageName: `${window.location.host}/appstore${to.path}`,
        });
      }

      if (to.query.q && (to.query.q !== from.query.q)) {
        trackSearch(to.query.q);
      }
    });

    this.$gdpr.showBanner();
  },

  methods: {
    setOptimisticDetailData(partialDetailData) {
      this.optimisticDetailData = partialDetailData;

      if (process.env.NODE_ENV === 'development') {
        console.log('Optimistic Detail Data: ', this.optimisticDetailData);
      }
    },

    handleWindowResize: throttle(function handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    }, 20),

    remoteDataFetched(dataName, success, response) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`${success ? '✅' : '❌'}${dataName}: `, response);
      }
    },

    async handleAppDetailViewWatch(to, from) {
      if (to.name !== 'app-detail') {
        this.detailData = null;
        this.optimisticDetailData = null;
        return;
      }

      if (to.name === from.name) {
        if (to.params.appName === from.params.appName) return;
      } else {
        this.detailViewBackRoute = from;
      }

      const appID = to.params.appName;

      if (appID === 'null') return;

      this.loadingDetailData = true;

      const QUERY = to.params.appType === 'app'
        ? APP_DETAIL(appID)
        : CONNECTOR_DETAIL(appID);

      try {
        const { data } = await axios.get(QUERY);

        this.detailData = data;
        this.loadingDetailData = false;

        this.remoteDataFetched('App Detail Data', true, data);
      } catch (err) {
        this.loadingDetailData = false;

        this.remoteDataFetched('App Detail Data', false, err);

        if (err.response.status === 404) {
          window.location.href = '/appstore';
        }
      }
    },

    async handleSearchViewWatch(to) {
      const viewsToLoadSearchViewData = ['search', 'home', 'app-detail'];

      if (
        this.isSearchViewDataCached
        || this.loadingApps
        || !viewsToLoadSearchViewData.includes(to.name)
      ) return;

      this.loadingApps = true;

      try {
        const { data } = await axios.get(ALL);

        this.appsData = data.apps;
        this.connectorsData = data.connectors;
        this.filtersData = data.filters;

        this.loadingApps = false;
        this.isSearchViewDataCached = true;

        this.remoteDataFetched('All Apps Data', true, data);
      } catch (err) {
        this.loadingApps = false;

        this.remoteDataFetched('All Apps Data', false, err);
      }
    },
  },
}).$mount('#app');
